import { log } from 'evlog/client';
import { CalendarSearch } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { redirect, useLoaderData, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { formatReservationDateTime } from '@virtual-care/isomorphic';
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@virtual-care/shadcn';
import {
  QuotaBanner,
  apiErrorToMessage,
  resolveErrorMessage,
  useAction,
} from '@virtual-care/ui';

import { requiresAuth } from '../../auth-loader.ts';
import {
  AppointmentPill,
  DetailPill,
  PaymentDetailsCard,
} from '../../components/booking/payment-card';
import { PromoCodeInput } from '../../components/booking/promo-code-input.tsx';
import Header from '../../components/header/header.tsx';
import ProfileCard from '../../components/header/profile-card.tsx';
import { useUser } from '../../firebase.tsx';
import { useReservation } from '../../hooks/reservation/context.tsx';
import {
  type StandardReservation,
  isStandardReady,
  standardReservationStore,
} from '../../hooks/reservation/standard.ts';
import { useBookingDetails } from '../../hooks/use-booking-details.ts';
import { usePaymentInfo } from '../../hooks/use-payment-info.ts';

type ConfirmationActionsProps = {
  onClick: () => void;
  label: string;
  isLoading?: boolean;
  disabled?: boolean;
};

const ConfirmationActions = ({
  onClick,
  label,
  isLoading,
  disabled,
}: ConfirmationActionsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <CardFooter className="flex w-full justify-center gap-4">
      <Button
        type="button"
        variant={'outline'}
        onClick={() => navigate(-1)}
        className="secondary-button"
      >
        {t('booking.bookingConfirmation.back')}
      </Button>
      <Button
        variant={'default'}
        onClick={onClick}
        disabled={isLoading || disabled}
        className="primary-button bg-green-600 hover:bg-green-700"
      >
        {isLoading ? t('booking.bookingConfirmation.processing') : label}
      </Button>
    </CardFooter>
  );
};

export default function BookingConfirmation() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const data = useLoaderData() as Awaited<
    ReturnType<typeof bookingConfirmationLoader>
  >;
  const isB2B = data.identity.userType === 'B2B';

  const createReservation = useAction('POST /appointment/b2b', {
    invalidate: ['GET /patients/me/reservations', 'GET /patients/me'],
  });
  const { data: identity } = useUser();
  const consultationQuota = identity?.b2bProfile?.usageLimit;
  const quotaKnown = consultationQuota !== undefined;
  const quotaExhausted = isB2B && quotaKnown && consultationQuota <= 0;

  const hasOnBehalfInfo = !!data.reservation.patientName;

  const bookingDetails = useBookingDetails(
    data.identity,
    t('common.services.standard'),
    hasOnBehalfInfo
      ? {
          patientName: data.reservation.patientName,
          guestEmail: data.reservation.guestEmail,
          patientBirthDate: data.reservation.patientBirthDate,
          patientGender: data.reservation.patientGender,
        }
      : undefined,
  );

  const formattedDateTime = formatReservationDateTime({
    scheduledDate: data.reservation.scheduledDate,
    minutesFrom: data.reservation.scheduledMinutes,
    timezone: data.reservation.timezone,
  });

  const [reservation, setReservation] = useReservation<StandardReservation>();
  const payment = usePaymentInfo({
    kind: 'standard',
    appliedPromo: reservation?.appliedPromo,
  });
  const isProcessing = isB2B && createReservation.isPending;

  const handleConfirm = async () => {
    log.info({ action: 'booking_confirm', isB2B });
    if (!isB2B) {
      return navigate('/book-appointment/payments');
    }

    const {
      specialityId,
      scheduledDate,
      scheduledMinutes,
      timezone,
      description,
      files,
      doctorName,
      patientName,
      guestEmail,
      patientBirthDate,
      patientGender,
    } = data.reservation;

    if (!doctorName) {
      toast.error(t('booking.bookingConfirmation.missingDoctorName'));
      return;
    }

    if (!guestEmail) {
      toast.error(t('booking.bookingConfirmation.missingGuestEmail'));
      return;
    }

    try {
      const result = await createReservation.mutateAsync({
        specialityId,
        scheduledDate,
        scheduledMinutes,
        timezone,
        description,
        doctorName,
        guestEmail,
        ...(files ? { files } : {}),
        ...(patientName ? { patientName } : {}),
        ...(patientBirthDate ? { patientBirthDate } : {}),
        ...(patientGender === 'male' || patientGender === 'female'
          ? { patientGender }
          : {}),
      });

      toast.success(
        t('booking.bookingConfirmation.submittedWithRemaining', {
          remaining: result.remainingUsageLimit,
        }),
      );
      navigate('/book-appointment/request-status?ok=1');
    } catch (error) {
      log.error({ action: 'booking_confirm', error });
      toast.error(resolveErrorMessage(apiErrorToMessage(error)));
    }
  };
  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-2">
            <div className="bg-sidebar-primary rounded-full p-2">
              <CalendarSearch className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-base font-bold text-gray-900 md:text-2xl">
              {t('common.services.standard')}
            </h1>
          </div>
        }
      >
        <ProfileCard />
      </Header>
      <div className="card-container">
        <Card className="card">
          <CardHeader className="text-center">
            <CardTitle className="text-xl font-bold">
              {t('booking.bookingConfirmation.title')}
            </CardTitle>
            <p className="text-sm text-gray-500">
              {t('booking.bookingConfirmation.subtitle')}
            </p>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            {isB2B && quotaKnown && (
              <QuotaBanner
                remaining={consultationQuota}
                label={t('booking.bookingConfirmation.remainingQuota')}
                hint={t('booking.bookingConfirmation.topUpHint')}
              />
            )}
            <div className="rounded-xl border bg-white p-6">
              <h3 className="mb-4 text-lg font-bold text-gray-900">
                {t('booking.bookingConfirmation.detailsHeader')}
              </h3>
              <div className="flex flex-wrap gap-3">
                {Object.entries(bookingDetails).map(([key, value]) => (
                  <DetailPill key={key} label={key} value={value} />
                ))}
                <AppointmentPill
                  label={t('booking.bookingConfirmation.appointmentPill')}
                  value={formattedDateTime}
                />
              </div>
            </div>
            {!isB2B && (
              <PromoCodeInput
                service="standard"
                applied={reservation?.appliedPromo}
                onApply={(applied) =>
                  setReservation((current) =>
                    current ? { ...current, appliedPromo: applied } : current,
                  )
                }
              />
            )}
            {!isB2B && <PaymentDetailsCard payment={payment} />}
          </CardContent>
          <ConfirmationActions
            onClick={handleConfirm}
            label={
              isB2B
                ? t('booking.bookingConfirmation.confirm')
                : t('booking.bookingConfirmation.payAndConfirm')
            }
            isLoading={isProcessing}
            disabled={quotaExhausted}
          />
        </Card>
      </div>
    </>
  );
}

export async function bookingConfirmationLoader() {
  const reservation = standardReservationStore.read();
  const identity = await requiresAuth();
  if (!reservation || !isStandardReady(reservation)) {
    throw redirect('/book-appointment');
  }
  return { reservation, identity };
}
